import { authFetch } from "@/lib/authFetch";
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  Users,
  LayoutDashboard,
  LogOut,
  UserPlus,
  Search,
  MoreHorizontal,
  Menu,
  X,
  FolderOpen,
  Calendar,
  Eye,
  EyeOff,
  Target,
  Settings,
  Upload,
} from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import Sidebar from "@/components/Sidebar";
import { BH_OFFICES } from "@/constants/locations";
import { ReadOnly } from "@/components/ReadOnly";
import { LinkedClientsDialog } from "@/components/LinkedClientsDialog";

const AFFILIATE_ROLES = ["REFERRAL_PARTNER", "BETENBOUGH_TEAM_MEMBER", "PREFERRED_LENDER"];
const isAffiliateUser = (u) => AFFILIATE_ROLES.includes(u?.role);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


// Composite value format for the per-office admin dropdown entries: `BH_LOCATION_ADMIN::<Location>`
const BH_LOCATION_ADMIN_OPTIONS = BH_OFFICES.map((loc) => ({
  value: `BH_LOCATION_ADMIN::${loc}`,
  label: `BH ${loc} Admin`,
  location: loc,
}));

// Splits a composite dropdown value (or plain role) into { role, location? }
const splitRoleValue = (val) => {
  if (typeof val === "string" && val.startsWith("BH_LOCATION_ADMIN::")) {
    return { role: "BH_LOCATION_ADMIN", location: val.split("::")[1] };
  }
  return { role: val };
};

// Builds the dropdown value for the current user (composite for location admin, plain otherwise)
const composeRoleValue = (userObj) => {
  if (userObj?.role === "BH_LOCATION_ADMIN" && userObj?.betenbough_location) {
    return `BH_LOCATION_ADMIN::${userObj.betenbough_location}`;
  }
  return userObj?.role;
};

const ROLES = [
  { value: "CLIENT", label: "Client" },
  { value: "REFERRAL_PARTNER", label: "Referral Partner" },
  { value: "BETENBOUGH_REP", label: "Betenbough Rep" },
  { value: "CREDIT_COUNSELOR", label: "Credit Counselor" },
  ...BH_LOCATION_ADMIN_OPTIONS,
  { value: "BH_ADMIN", label: "BH Admin (All Offices)" },
  { value: "BETENBOUGH_ADMIN", label: "Admin" },
  { value: "CREDITFIRMINC_ADMIN", label: "CFI Admin" },
];

// Edit dialog also needs the existing affiliate roles so current values display
const EDIT_ROLES = [...ROLES, { value: "PREFERRED_LENDER", label: "Preferred Lender" }, { value: "BETENBOUGH_TEAM_MEMBER", label: "Referral Partner (legacy)" }];

// Credit Firm Inc Locations
const CFI_LOCATIONS = [
  "Amarillo, TX",
  "Lubbock, TX",
  "Midland, TX",
  "Odessa, TX",
  "Abilene, TX",
  "San Angelo, TX",
  "Austin, TX",
  "Waco, TX",
  "Tyler, TX",
  "Roswell, NM",
  "Hobbs, NM",
  "Las Cruces, NM",
  "Charlotte, NC",
  "Online Only",
  "Other",
];

// Role Badge
const RoleBadge = ({ role, location }) => {
  const roleStyles = {
    CLIENT: "bg-slate-100 text-slate-800",
    BETENBOUGH_TEAM_MEMBER: "bg-blue-100 text-blue-800",
    REFERRAL_PARTNER: "bg-blue-100 text-blue-800",
    BETENBOUGH_REP: "bg-teal-100 text-teal-800",
    CREDIT_COUNSELOR: "bg-orange-100 text-orange-800",
    BH_ADMIN: "bg-indigo-100 text-indigo-800",
    BH_LOCATION_ADMIN: "bg-indigo-100 text-indigo-800",
    BETENBOUGH_ADMIN: "bg-purple-100 text-purple-800",
    CREDITFIRMINC_ADMIN: "bg-teal-100 text-teal-800",
  };

  const roleLabels = {
    CLIENT: "Client",
    BETENBOUGH_TEAM_MEMBER: "Referral Partner",
    REFERRAL_PARTNER: "Referral Partner",
    BETENBOUGH_REP: "Betenbough Rep",
    CREDIT_COUNSELOR: "Credit Counselor",
    BH_ADMIN: "BH Admin",
    BETENBOUGH_ADMIN: "Admin",
    CREDITFIRMINC_ADMIN: "CFI Admin",
  };

  const label =
    role === "BH_LOCATION_ADMIN"
      ? `BH ${location || "Location"} Admin`
      : roleLabels[role] || role;

  return (
    <Badge variant="outline" className={roleStyles[role] || roleStyles.CLIENT}>
      {label}
    </Badge>
  );
};

const UserManagement = () => {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [linkedUser, setLinkedUser] = useState(null);
  const [linkCounts, setLinkCounts] = useState(null);
  const isCfiAdmin = user?.role === "CREDITFIRMINC_ADMIN";
  useEffect(() => {
    if (!isCfiAdmin) return undefined;
    const load = (refresh) => authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/linked-counts${refresh ? "?refresh=true" : ""}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null)).then((d) => d && setLinkCounts(d.counts)).catch(() => {});
    load();
    const h = () => load(true);
    window.addEventListener("link-job-done", h);
    return () => window.removeEventListener("link-job-done", h);
  }, [isCfiAdmin]);
  const [showPassword, setShowPassword] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [resettingPassword, setResettingPassword] = useState(false);
  const [addUserTab, setAddUserTab] = useState("manual");
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  
  // Check if current user can add users (Admins and Credit Counselors only)
  const canAddUsers = user?.role === "BETENBOUGH_ADMIN" || 
                      user?.role === "BH_ADMIN" ||
                      user?.role === "CREDITFIRMINC_ADMIN" || 
                      user?.role === "CREDIT_COUNSELOR";
  
  // Check if current user is an admin (can create admin users)
  const isUserAdmin = user?.role === "BETENBOUGH_ADMIN" || user?.role === "BH_ADMIN" || user?.role === "CREDITFIRMINC_ADMIN";
  
  // Manual user form
  const [manualUserForm, setManualUserForm] = useState({
    email: "",
    name: "",
    password: "",
    role: "REFERRAL_PARTNER",
    betenbough_location: "",
    phone: "",
  });
  
  // Invite user form
  const [inviteForm, setInviteForm] = useState({
    email: "",
    name: "",
    role: "REFERRAL_PARTNER",
    betenbough_location: "",
  });
  
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) {
        fetchUsers(searchQuery);
      } else {
        fetchUsers();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchUsers = async (search = "") => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (search) {
        params.append("search", search);
      }
      const response = await authFetch(`${API}/users?${params}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateManualUser = async () => {
    if (!manualUserForm.email || !manualUserForm.name || !manualUserForm.password) {
      toast.error("Please fill all required fields");
      return;
    }

    if (manualUserForm.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setSubmitting(true);
    try {
      const response = await authFetch(`${API}/users/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(manualUserForm),
      });

      if (response.ok) {
        toast.success(`User ${manualUserForm.email} created successfully`);
        setAddUserDialogOpen(false);
        setManualUserForm({
          email: "",
          name: "",
          password: "",
          role: "REFERRAL_PARTNER",
          betenbough_location: "",
          phone: "",
        });
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create user");
      }
    } catch (error) {
      console.error("Create user error:", error);
      toast.error("Failed to create user");
    } finally {
      setSubmitting(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteForm.email || !inviteForm.name || !inviteForm.role) {
      toast.error("Please fill all required fields");
      return;
    }

    setSubmitting(true);
    try {
      const response = await authFetch(`${API}/users/invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(inviteForm),
      });

      if (response.ok) {
        toast.success(`Invited ${inviteForm.email}`);
        setAddUserDialogOpen(false);
        setInviteForm({
          email: "",
          name: "",
          role: "REFERRAL_PARTNER",
          betenbough_location: "",
        });
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to invite user");
      }
    } catch (error) {
      console.error("Invite error:", error);
      toast.error("Failed to invite user");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    setSubmitting(true);
    try {
      const response = await authFetch(`${API}/users/${selectedUser.user_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          role: selectedUser.role,
          betenbough_location: selectedUser.betenbough_location,
          is_active: selectedUser.is_active,
          name: selectedUser.name,
          email: selectedUser.email,
          is_preferred_lender: selectedUser.is_preferred_lender || false,
        }),
      });

      if (response.ok) {
        toast.success("User updated successfully");
        setEditDialogOpen(false);
        setSelectedUser(null);
        fetchUsers();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || "Failed to update user");
      }
    } catch (error) {
      console.error("Update error:", error);
      toast.error("Failed to update user");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (targetUser) => {
    try {
      const response = await authFetch(`${API}/users/${targetUser.user_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ is_active: !targetUser.is_active }),
      });

      if (response.ok) {
        toast.success(targetUser.is_active ? "User deactivated" : "User activated");
        fetchUsers();
      } else {
        toast.error("Failed to update user status");
      }
    } catch (error) {
      console.error("Toggle error:", error);
      toast.error("Failed to update user status");
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setResettingPassword(true);
    try {
      const response = await authFetch(`${API}/admin/reset-user-password/${resetPasswordUser.user_id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ new_password: newPassword }),
      });
      if (response.ok) {
        toast.success(`Password reset for ${resetPasswordUser.name}`);
        setResetPasswordDialogOpen(false);
        setResetPasswordUser(null);
        setNewPassword("");
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to reset password");
      }
    } catch (error) {
      console.error("Reset password error:", error);
      toast.error("Failed to reset password");
    } finally {
      setResettingPassword(false);
    }
  };

  // Users are now filtered server-side, just use them directly
  const filteredUsers = users;

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.csv')) {
      toast.error("Please upload a CSV file");
      return;
    }
    
    setBulkUploading(true);
    setBulkResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await authFetch(`${API}/admin/bulk-upload-users`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      
      const data = await response.json().catch(() => null);
      if (response.ok || (data && data.success)) {
        setBulkResult(data);
        toast.success(data?.message || "Bulk upload complete!");
        fetchUsers();
      } else {
        toast.error(data?.detail || "Bulk upload failed");
      }
    } catch (error) {
      console.error("Bulk upload error:", error);
      toast.error("Bulk upload failed");
    } finally {
      setBulkUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className={`flex min-h-screen transition-colors duration-300 ${isDarkMode ? 'bg-black' : 'bg-slate-50'}`}>
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isAdmin={true} />

      <main className="flex-1 min-w-0 md:ml-64">
        <header className={`sticky top-0 z-30 px-4 md:px-6 py-4 border-b transition-colors duration-300 ${isDarkMode ? 'bg-black border-[#1a3d18]' : 'header-glass'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className={`md:hidden p-2 rounded-lg transition-colors ${isDarkMode ? 'hover:bg-[#22c55e]/20 text-white' : 'hover:bg-slate-100'}`}
              >
                <Menu className="h-5 w-5" />
              </button>
              <div>
                <h1 className={`text-xl md:text-2xl font-bold transition-colors ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>User Management</h1>
                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Manage portal users and permissions</p>
              </div>
            </div>
            {canAddUsers && (
              <Dialog open={addUserDialogOpen} onOpenChange={setAddUserDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-user-btn">
                    <UserPlus className="h-4 w-4 mr-2" />
                    Add User
                  </Button>
                </DialogTrigger>
              <DialogContent className={`max-w-md ${isDarkMode ? 'bg-[#265324] border-[#22c55e]' : ''}`}>
                <DialogHeader>
                  <DialogTitle className={isDarkMode ? 'text-white' : ''}>Add New User</DialogTitle>
                  <DialogDescription className={isDarkMode ? 'text-slate-300' : ''}>
                    Create a new user manually or send an invitation
                  </DialogDescription>
                </DialogHeader>
                
                <Tabs value={addUserTab} onValueChange={setAddUserTab} className="w-full">
                  <TabsList className={`grid w-full grid-cols-3 ${isDarkMode ? 'bg-[#1a3d18]' : ''}`}>
                    <TabsTrigger value="manual" className={isDarkMode ? 'data-[state=active]:bg-[#22c55e] data-[state=active]:text-white text-slate-300' : ''}>Manual</TabsTrigger>
                    <TabsTrigger value="invite" className={isDarkMode ? 'data-[state=active]:bg-[#22c55e] data-[state=active]:text-white text-slate-300' : ''}>Invite</TabsTrigger>
                    {isUserAdmin && (
                      <TabsTrigger value="bulk" data-testid="bulk-upload-tab" className={isDarkMode ? 'data-[state=active]:bg-[#22c55e] data-[state=active]:text-white text-slate-300' : ''}>Bulk CSV</TabsTrigger>
                    )}
                  </TabsList>
                  
                  <TabsContent value="manual" className="space-y-4 py-4">
                    <div>
                      <Label className={isDarkMode ? 'text-white' : ''}>Email *</Label>
                      <Input
                        type="email"
                        value={manualUserForm.email}
                        onChange={(e) => setManualUserForm({ ...manualUserForm, email: e.target.value })}
                        className={`mt-1 ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`}
                        data-testid="manual-email-input"
                      />
                    </div>
                    <div>
                      <Label className={isDarkMode ? 'text-white' : ''}>Name *</Label>
                      <Input
                        value={manualUserForm.name}
                        onChange={(e) => setManualUserForm({ ...manualUserForm, name: e.target.value })}
                        className={`mt-1 ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`}
                        data-testid="manual-name-input"
                      />
                    </div>
                    <div>
                      <Label className={isDarkMode ? 'text-white' : ''}>Password *</Label>
                      <div className="relative mt-1">
                        <Input
                          type={showPassword ? "text" : "password"}
                          value={manualUserForm.password}
                          onChange={(e) => setManualUserForm({ ...manualUserForm, password: e.target.value })}
                          className={`pr-10 ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`}
                          data-testid="manual-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-slate-300 hover:text-white' : 'text-slate-400 hover:text-slate-600'}`}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-500'}`}>Minimum 6 characters</p>
                    </div>
                    <div>
                      <Label className={isDarkMode ? 'text-white' : ''}>Phone <span className="text-xs text-slate-400">(optional)</span></Label>
                      <Input
                        type="tel"
                        value={manualUserForm.phone}
                        onChange={(e) => setManualUserForm({ ...manualUserForm, phone: e.target.value })}
                        placeholder="e.g. 555-123-4567"
                        className={`mt-1 ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`}
                        data-testid="manual-phone-input"
                      />
                    </div>
                    <div>
                      <Label className={isDarkMode ? 'text-white' : ''}>Role *</Label>
                      <Select
                        value={composeRoleValue(manualUserForm)}
                        onValueChange={(v) => {
                          const { role, location } = splitRoleValue(v);
                          setManualUserForm({
                            ...manualUserForm,
                            role,
                            ...(location !== undefined ? { betenbough_location: location } : {}),
                          });
                        }}
                      >
                        <SelectTrigger className={`mt-1 ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`} data-testid="manual-role-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className={isDarkMode ? 'bg-[#1a3d18] border-[#22c55e]' : ''}>
                          <SelectItem value="REFERRAL_PARTNER" className={isDarkMode ? 'text-white hover:bg-[#22c55e]/20' : ''}>Referral Partner</SelectItem>
                          <SelectItem value="BETENBOUGH_REP" className={isDarkMode ? 'text-white hover:bg-[#22c55e]/20' : ''}>Betenbough Rep</SelectItem>
                          <SelectItem value="CREDIT_COUNSELOR" className={isDarkMode ? 'text-white hover:bg-[#22c55e]/20' : ''}>Credit Counselor</SelectItem>
                          {isUserAdmin && BH_LOCATION_ADMIN_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value} className={isDarkMode ? 'text-white hover:bg-[#22c55e]/20' : ''}>{opt.label}</SelectItem>
                          ))}
                          {isUserAdmin && (
                            <SelectItem value="BH_ADMIN" className={isDarkMode ? 'text-white hover:bg-[#22c55e]/20' : ''}>BH Admin (All Offices)</SelectItem>
                          )}
                          {isUserAdmin && (
                            <SelectItem value="BETENBOUGH_ADMIN" className={isDarkMode ? 'text-white hover:bg-[#22c55e]/20' : ''}>Admin</SelectItem>
                          )}
                          {isUserAdmin && (
                            <SelectItem value="CREDITFIRMINC_ADMIN" className={isDarkMode ? 'text-white hover:bg-[#22c55e]/20' : ''}>CFI Admin</SelectItem>
                          )}
                          <SelectItem value="CLIENT" className={isDarkMode ? 'text-white hover:bg-[#22c55e]/20' : ''}>Client</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className={isDarkMode ? 'text-white' : ''}>Location</Label>
                      <Select
                        value={manualUserForm.betenbough_location}
                        onValueChange={(v) => setManualUserForm({ ...manualUserForm, betenbough_location: v === "none" ? "" : v })}
                      >
                        <SelectTrigger className={`mt-1 ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`}>
                          <SelectValue placeholder="Select location (optional)" />
                        </SelectTrigger>
                        <SelectContent className={isDarkMode ? 'bg-[#1a3d18] border-[#22c55e]' : ''}>
                          <SelectItem value="none" className={isDarkMode ? 'text-white hover:bg-[#22c55e]/20' : ''}>None</SelectItem>
                          {CFI_LOCATIONS.map((loc) => (
                            <SelectItem key={loc} value={loc} className={isDarkMode ? 'text-white hover:bg-[#22c55e]/20' : ''}>{loc}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <DialogFooter className="pt-4">
                      <Button variant="outline" onClick={() => setAddUserDialogOpen(false)} className={isDarkMode ? 'border-[#22c55e] text-slate-300 hover:bg-[#22c55e]/20' : ''}>Cancel</Button>
                      <Button onClick={handleCreateManualUser} disabled={submitting} data-testid="create-user-btn">
                        {submitting ? "Creating..." : "Create User"}
                      </Button>
                    </DialogFooter>
                  </TabsContent>
                  
                  <TabsContent value="invite" className="space-y-4 py-4">
                    <div>
                      <Label className={isDarkMode ? 'text-white' : ''}>Email *</Label>
                      <Input
                        type="email"
                        value={inviteForm.email}
                        onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                        className={`mt-1 ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`}
                        data-testid="invite-email-input"
                      />
                    </div>
                    <div>
                      <Label className={isDarkMode ? 'text-white' : ''}>Name *</Label>
                      <Input
                        value={inviteForm.name}
                        onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                        className={`mt-1 ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`}
                        data-testid="invite-name-input"
                      />
                    </div>
                    <div>
                      <Label className={isDarkMode ? 'text-white' : ''}>Role *</Label>
                      <Select
                        value={composeRoleValue(inviteForm)}
                        onValueChange={(v) => {
                          const { role, location } = splitRoleValue(v);
                          setInviteForm({
                            ...inviteForm,
                            role,
                            ...(location !== undefined ? { betenbough_location: location } : {}),
                          });
                        }}
                      >
                        <SelectTrigger className={`mt-1 ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`} data-testid="invite-role-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className={isDarkMode ? 'bg-[#1a3d18] border-[#22c55e]' : ''}>
                          <SelectItem value="REFERRAL_PARTNER" className={isDarkMode ? 'text-white hover:bg-[#22c55e]/20' : ''}>Referral Partner</SelectItem>
                          <SelectItem value="BETENBOUGH_REP" className={isDarkMode ? 'text-white hover:bg-[#22c55e]/20' : ''}>Betenbough Rep</SelectItem>
                          <SelectItem value="CREDIT_COUNSELOR" className={isDarkMode ? 'text-white hover:bg-[#22c55e]/20' : ''}>Credit Counselor</SelectItem>
                          {isUserAdmin && BH_LOCATION_ADMIN_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value} className={isDarkMode ? 'text-white hover:bg-[#22c55e]/20' : ''}>{opt.label}</SelectItem>
                          ))}
                          {isUserAdmin && (
                            <SelectItem value="BH_ADMIN" className={isDarkMode ? 'text-white hover:bg-[#22c55e]/20' : ''}>BH Admin (All Offices)</SelectItem>
                          )}
                          {isUserAdmin && (
                            <SelectItem value="BETENBOUGH_ADMIN" className={isDarkMode ? 'text-white hover:bg-[#22c55e]/20' : ''}>Admin</SelectItem>
                          )}
                          {isUserAdmin && (
                            <SelectItem value="CREDITFIRMINC_ADMIN" className={isDarkMode ? 'text-white hover:bg-[#22c55e]/20' : ''}>CFI Admin</SelectItem>
                          )}
                          <SelectItem value="CLIENT" className={isDarkMode ? 'text-white hover:bg-[#22c55e]/20' : ''}>Client</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className={isDarkMode ? 'text-white' : ''}>Location</Label>
                      <Select
                        value={inviteForm.betenbough_location}
                        onValueChange={(v) => setInviteForm({ ...inviteForm, betenbough_location: v === "none" ? "" : v })}
                      >
                        <SelectTrigger className={`mt-1 ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`}>
                          <SelectValue placeholder="Select location (optional)" />
                        </SelectTrigger>
                        <SelectContent className={isDarkMode ? 'bg-[#1a3d18] border-[#22c55e]' : ''}>
                          <SelectItem value="none" className={isDarkMode ? 'text-white hover:bg-[#22c55e]/20' : ''}>None</SelectItem>
                          {CFI_LOCATIONS.map((loc) => (
                            <SelectItem key={loc} value={loc} className={isDarkMode ? 'text-white hover:bg-[#22c55e]/20' : ''}>{loc}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <DialogFooter className="pt-4">
                      <Button variant="outline" onClick={() => setAddUserDialogOpen(false)} className={isDarkMode ? 'border-[#22c55e] text-slate-300 hover:bg-[#22c55e]/20' : ''}>Cancel</Button>
                      <Button onClick={handleInvite} disabled={submitting} data-testid="send-invite-btn">
                        {submitting ? "Sending..." : "Send Invite"}
                      </Button>
                    </DialogFooter>
                  </TabsContent>

                  {/* Bulk Upload Tab */}
                  {isUserAdmin && (
                    <TabsContent value="bulk" className="space-y-4 py-4">
                      <div className={`rounded-lg border-2 border-dashed p-6 text-center ${isDarkMode ? 'border-[#22c55e]/40 bg-[#1a3d18]/50' : 'border-slate-300 bg-slate-50'}`}>
                        <Upload className={`mx-auto h-10 w-10 mb-3 ${isDarkMode ? 'text-[#22c55e]' : 'text-slate-400'}`} />
                        <p className={`text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-slate-700'}`}>
                          Upload a CSV file to add multiple users
                        </p>
                        <p className={`text-xs mb-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          Required columns: <strong>name</strong>, <strong>email</strong>, <strong>contact_type</strong>
                          <br />Optional: company, phone
                        </p>
                        <div className="flex flex-col items-center gap-3">
                          <label
                            htmlFor="bulk-csv-upload"
                            data-testid="bulk-upload-input-label"
                            className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium cursor-pointer transition-colors ${
                              bulkUploading ? 'opacity-50 pointer-events-none' : ''
                            } ${isDarkMode ? 'bg-[#22c55e] text-white hover:bg-[#22c55e]/80' : 'bg-[#357c2a] text-white hover:bg-[#2a5f5a]'}`}
                          >
                            <Upload className="h-4 w-4" />
                            {bulkUploading ? "Uploading..." : "Choose CSV File"}
                          </label>
                          <input
                            id="bulk-csv-upload"
                            type="file"
                            accept=".csv"
                            onChange={handleBulkUpload}
                            disabled={bulkUploading}
                            className="hidden"
                            data-testid="bulk-upload-file-input"
                          />
                          <button
                            type="button"
                            data-testid="download-sample-csv"
                            onClick={() => {
                              const csv = "name,email,contact_type,company,phone\nJohn Doe,john@example.com,affiliate,Acme Realty,555-0100\nJane Smith,jane@example.com,lender,First National,555-0200\n";
                              const blob = new Blob([csv], { type: "text/csv" });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement("a");
                              a.href = url;
                              a.download = "sample_users.csv";
                              a.click();
                              URL.revokeObjectURL(url);
                            }}
                            className={`text-xs underline ${isDarkMode ? 'text-[#22c55e]' : 'text-[#357c2a]'}`}
                          >
                            Download sample CSV template
                          </button>
                        </div>
                      </div>

                      {/* Bulk Upload Results */}
                      {bulkResult && (
                        <div className={`rounded-lg p-4 text-sm space-y-2 ${isDarkMode ? 'bg-[#1a3d18] border border-[#22c55e]/30' : 'bg-white border'}`}>
                          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`} data-testid="bulk-upload-result-message">
                            {bulkResult.message}
                          </p>
                          <div className="flex gap-4">
                            <span className="text-green-600 font-medium" data-testid="bulk-created-count">
                              {bulkResult.created} created
                            </span>
                            <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'} data-testid="bulk-skipped-count">
                              {bulkResult.skipped} skipped
                            </span>
                          </div>
                          {bulkResult.errors?.length > 0 && (
                            <div className="mt-2">
                              <p className="text-red-500 font-medium text-xs mb-1">Errors:</p>
                              <ul className="text-xs text-red-400 space-y-0.5 max-h-24 overflow-y-auto">
                                {bulkResult.errors.map((err, i) => (
                                  <li key={i}>{err}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}

                      <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        All uploaded users will be created with the default password <strong>CfiUser1!</strong> and can log in immediately.
                        <br />Valid contact types: <em>affiliate, lender, rep, counselor, admin</em>
                      </p>
                    </TabsContent>
                  )}
                </Tabs>
              </DialogContent>
            </Dialog>
            )}
          </div>
        </header>

        <div className="p-4 md:p-6">
          <Card data-testid="users-table-card" className={isDarkMode ? 'bg-[#265324] border-[#1a3d18]' : ''}>
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <CardTitle className={`text-lg ${isDarkMode ? 'text-white' : ''}`}>All Users</CardTitle>
                <div className="relative">
                  <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-400'}`} />
                  <Input
                    placeholder="Search users..."
                    className={`pl-9 w-full md:w-64 ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white placeholder:text-slate-400' : ''}`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    data-testid="search-users-input"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className={isDarkMode ? 'border-[#1a3d18]' : ''}>
                      <TableHead className={isDarkMode ? 'text-slate-300' : ''}>User</TableHead>
                      <TableHead className={isDarkMode ? 'text-slate-300' : ''}>Role</TableHead>
                      <TableHead className={isDarkMode ? 'text-slate-300' : ''}>Location</TableHead>
                      <TableHead className={isDarkMode ? 'text-slate-300' : ''}>Status</TableHead>
                      <TableHead className={`text-right ${isDarkMode ? 'text-slate-300' : ''}`}>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      [...Array(5)].map((_, i) => (
                        <TableRow key={i} className={isDarkMode ? 'border-[#1a3d18]' : ''}>
                          <TableCell><Skeleton className="h-10 w-48" /></TableCell>
                          <TableCell><Skeleton className="h-6 w-24" /></TableCell>
                          <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                          <TableCell><Skeleton className="h-6 w-16" /></TableCell>
                          <TableCell><Skeleton className="h-8 w-8 ml-auto" /></TableCell>
                        </TableRow>
                      ))
                    ) : filteredUsers.length > 0 ? (
                      filteredUsers.map((u) => (
                        <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`} className={isDarkMode ? 'border-[#1a3d18] hover:bg-[#1a3d18]' : ''}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              {u.picture ? (
                                <img src={u.picture} alt={u.name} className="w-10 h-10 rounded-full" />
                              ) : (
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-[#1a3d18]' : 'bg-slate-200'}`}>
                                  <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-600'}`}>{u.name?.charAt(0) || "?"}</span>
                                </div>
                              )}
                              <div>
                                <p className={`font-medium ${isDarkMode ? 'text-white' : ''}`}>{u.name}</p>
                                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{u.email}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell><RoleBadge role={u.role} location={u.betenbough_location} /></TableCell>
                          <TableCell className={isDarkMode ? 'text-slate-300' : ''}>{u.betenbough_location || "-"}</TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={u.is_active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}
                            >
                              {u.is_active ? "Active" : "Inactive"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className={isDarkMode ? 'text-white hover:bg-[#22c55e]/20' : ''}>
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className={isDarkMode ? 'bg-[#1a3d18] border-[#22c55e]' : ''}>
                                <DropdownMenuItem
                                  onClick={() => {
                                    setSelectedUser({ ...u });
                                    setEditDialogOpen(true);
                                  }}
                                  className={isDarkMode ? 'text-white hover:bg-[#22c55e]/20' : ''}
                                >
                                  Edit User
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleToggleActive(u)}
                                  className={u.is_active ? "text-red-600" : "text-green-600"}
                                >
                                  {u.is_active ? "Deactivate" : "Activate"}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => {
                                    setResetPasswordUser(u);
                                    setNewPassword("");
                                    setResetPasswordDialogOpen(true);
                                  }}
                                  className={isDarkMode ? 'text-white hover:bg-[#22c55e]/20' : ''}
                                  data-testid={`reset-password-${u.user_id}`}
                                >
                                  Reset Password
                                </DropdownMenuItem>
                                {isCfiAdmin && <DropdownMenuItem
                                  onClick={() => setLinkedUser(u)}
                                  className={isDarkMode ? 'text-white hover:bg-[#22c55e]/20' : ''}
                                  data-testid={`linked-clients-${u.user_id}`}
                                >
                                  Linked Clients{linkCounts ? ` (${linkCounts[u.user_id] || 0})` : ""}
                                </DropdownMenuItem>}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow className={isDarkMode ? 'border-[#1a3d18]' : ''}>
                        <TableCell colSpan={5} className={`text-center py-8 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          No users found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Edit User Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
              <DialogDescription>Update user role and permissions</DialogDescription>
            </DialogHeader>
            {selectedUser && (
              <div className="space-y-4 py-4">
                {isAffiliateUser(selectedUser) && (
                  <p className="text-xs rounded-md bg-amber-50 text-amber-800 p-2" data-testid="edit-user-affiliate-note">
                    Name, email and location are managed in{" "}
                    <Link to={`/admin/affiliates?q=${encodeURIComponent(selectedUser.email || "")}`} className="underline font-medium" data-testid="edit-user-affiliate-link">Affiliate Management</Link>.
                  </p>
                )}
                <ReadOnly locked={isAffiliateUser(selectedUser)} testId="edit-user-email-readonly">
                  <Label>Email</Label>
                  <Input
                    data-testid="edit-user-email"
                    value={selectedUser.email || ""}
                    onChange={(e) => setSelectedUser({ ...selectedUser, email: e.target.value })}
                    className="mt-1"
                    placeholder="user@example.com"
                  />
                </ReadOnly>
                <ReadOnly locked={isAffiliateUser(selectedUser)} testId="edit-user-name-readonly">
                  <Label>Name</Label>
                  <Input
                    data-testid="edit-user-name"
                    value={selectedUser.name || ""}
                    onChange={(e) => setSelectedUser({ ...selectedUser, name: e.target.value })}
                    className="mt-1"
                  />
                </ReadOnly>
                <div>
                  <Label>Role</Label>
                  <Select
                    value={composeRoleValue(selectedUser)}
                    onValueChange={(v) => {
                      const { role, location } = splitRoleValue(v);
                      setSelectedUser({
                        ...selectedUser,
                        role,
                        ...(location !== undefined ? { betenbough_location: location } : {}),
                      });
                    }}
                  >
                    <SelectTrigger className="mt-1" data-testid="edit-user-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EDIT_ROLES.map((role) => (
                        <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <ReadOnly locked={isAffiliateUser(selectedUser)} testId="edit-user-location-readonly">
                  <Label>Location</Label>
                  <Select
                    value={selectedUser.betenbough_location || "none"}
                    onValueChange={(v) => setSelectedUser({ ...selectedUser, betenbough_location: v === "none" ? "" : v })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {selectedUser.betenbough_location && !CFI_LOCATIONS.includes(selectedUser.betenbough_location) && (
                        <SelectItem value={selectedUser.betenbough_location}>{selectedUser.betenbough_location}</SelectItem>
                      )}
                      {CFI_LOCATIONS.map((loc) => (
                        <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </ReadOnly>
                {(selectedUser.role === "REFERRAL_PARTNER" || selectedUser.role === "BETENBOUGH_TEAM_MEMBER" || selectedUser.role === "PREFERRED_LENDER") && (
                  <div className="flex items-center gap-3 pt-2">
                    <input
                      type="checkbox"
                      id="is_preferred_lender"
                      checked={selectedUser.is_preferred_lender || false}
                      onChange={(e) => setSelectedUser({ ...selectedUser, is_preferred_lender: e.target.checked })}
                      className="h-4 w-4 rounded border-gray-300"
                      data-testid="edit-user-preferred-lender-checkbox"
                    />
                    <Label htmlFor="is_preferred_lender" className="cursor-pointer">
                      BH Preferred Lender
                      <span className="text-xs text-slate-500 ml-2">(Appears in BH form Preferred Lender dropdown)</span>
                    </Label>
                  </div>
                )}
              </div>
            )}
            <DialogFooter className="sm:justify-between">
              {isCfiAdmin ? <Button variant="secondary" onClick={() => setLinkedUser(selectedUser)} data-testid="edit-user-linked-clients-btn">Linked Clients{linkCounts && selectedUser ? ` (${linkCounts[selectedUser.user_id] || 0})` : ""}</Button> : <span />}
              <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setEditDialogOpen(false); setSelectedUser(null); }}>Cancel</Button>
              <Button onClick={handleUpdateUser} disabled={submitting}>
                {submitting ? "Saving..." : "Save Changes"}
              </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <LinkedClientsDialog person={linkedUser} onClose={() => setLinkedUser(null)} />

        {/* Reset Password Dialog */}
        <Dialog open={resetPasswordDialogOpen} onOpenChange={(open) => { setResetPasswordDialogOpen(open); if (!open) { setResetPasswordUser(null); setNewPassword(""); } }}>
          <DialogContent className={`max-w-sm ${isDarkMode ? 'bg-[#265324] border-[#22c55e]' : ''}`}>
            <DialogHeader>
              <DialogTitle className={isDarkMode ? 'text-white' : ''}>Reset Password</DialogTitle>
              <DialogDescription className={isDarkMode ? 'text-slate-300' : ''}>
                Set a new password for {resetPasswordUser?.name} ({resetPasswordUser?.email})
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label className={isDarkMode ? 'text-slate-300' : ''}>New Password</Label>
                <Input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 6 chars)"
                  className={`mt-1 ${isDarkMode ? 'bg-[#1a3d18] border-[#22c55e] text-white' : ''}`}
                  data-testid="reset-password-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setResetPasswordDialogOpen(false); setResetPasswordUser(null); setNewPassword(""); }} className={isDarkMode ? 'border-[#22c55e] text-slate-300 hover:bg-[#22c55e]/20' : ''}>Cancel</Button>
              <Button onClick={handleResetPassword} disabled={resettingPassword || !newPassword || newPassword.length < 6} data-testid="reset-password-submit">
                {resettingPassword ? "Resetting..." : "Reset Password"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default UserManagement;
